import React, { useCallback, useEffect, useState } from 'react';
import Button from '../ui/Button';
import Icon from '../Icon';
import { addVendorCategory, listVendorCategories, removeVendorCategory, type VendorCategoryRecord } from '../../services/VendorCategoryService';
import { errorMessageOf } from '../../utils/apiErrors';

/**
 * The workspace's vendor categories (one list shared by every project). Each add and remove is its own
 * API call, so saving never rewrites the whole list and cannot archive a category someone else just added.
 */
const WorkspaceVendorCategories: React.FC = () => {
  const [categories, setCategories] = useState<VendorCategoryRecord[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoadFailed(false);
    try {
      setCategories(await listVendorCategories());
    } catch {
      setLoadFailed(true);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const change = async (action: () => Promise<void>, fallback: string) => {
    setBusy(true);
    setError('');
    try {
      await action();
      setNewName('');
    } catch (err) {
      setError(errorMessageOf(err, fallback));
    }
    await load();
    setBusy(false);
  };

  const trimmed = newName.trim();
  const duplicate = (categories ?? []).some(c => c.name.toLowerCase() === trimmed.toLowerCase());

  return (
    <div className="settings-card" id="vendor-categories">
      <h4 className="settings-card-title">Vendor categories</h4>
      <p className="settings-card-hint">The same list is used in every project of this workspace.</p>

      {loadFailed && (
        <div className="settings-inline-error" role="alert">
          <span>Couldn't load vendor categories.</span>
          <Button variant="secondary" size="sm" onClick={() => void load()}>Retry</Button>
        </div>
      )}
      {error && <div className="settings-inline-error" role="alert">{error}</div>}

      {categories && (
        <>
          {categories.length === 0 ? (
            <p className="settings-card-hint">No categories yet.</p>
          ) : (
            <ul className="wsvc-list">
              {categories.map(c => (
                <li key={c.id} className="tag-pill wsvc-item">
                  <span className="wsvc-name">{c.name}</span>
                  <button
                    type="button"
                    className="wsvc-remove"
                    aria-label={`Remove ${c.name}`}
                    disabled={busy}
                    onClick={() => void change(() => removeVendorCategory(c.id), "Couldn't remove the category. Please try again.")}
                  >
                    <Icon name="x" size={12} />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="wsvc-add">
            <div className="form-group settings-new-tag-name wsvc-field">
              <input
                type="text"
                className="form-control"
                aria-label="New vendor category"
                placeholder="New category"
                value={newName}
                maxLength={80}
                onChange={e => setNewName(e.target.value)}
              />
            </div>
            <Button
              variant="primary"
              size="sm"
              disabled={busy || !trimmed || duplicate}
              onClick={() => void change(() => addVendorCategory(trimmed), "Couldn't add the category. Please try again.")}
            >
              Add category
            </Button>
          </div>
          {duplicate && trimmed && <p className="settings-card-hint">That category already exists.</p>}
        </>
      )}
    </div>
  );
};

export default WorkspaceVendorCategories;
